import React from 'react';
import {useTranslation} from 'react-i18next';
import {ShieldCheck, ShieldAlert, Timer} from 'lucide-react';
import type {UserRestrictionDTO} from './AccountStanding';

interface StandingStatsProps {
    data: UserRestrictionDTO;
}

const StandingStats = ({data}: StandingStatsProps) => {
    const {t} = useTranslation();

    const protectionPercent = data.excellentProtectionTotalPoints > 0
        ? Math.round((data.excellentProtectionRemainingPoints / data.excellentProtectionTotalPoints) * 100)
        : 0;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

            <div className="rounded-xl border border-emerald-500/10 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={15} className="text-emerald-500"/>
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                        {t('dashboard.profile.standing.excellent_protection')}
                    </span>
                </div>

                <div className="flex items-end gap-2">
                    <span className="text-3xl font-black text-emerald-500">
                        {data.excellentProtectionRemainingPoints}
                    </span>

                    <span className="text-[10px] text-gray-500 mb-1">
                        / {data.excellentProtectionTotalPoints}
                    </span>
                </div>

                <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-emerald-500 transition-all duration-500"
                        style={{width: `${protectionPercent}%`}}
                    />
                </div>

                <p className="text-[10px] text-gray-500 mt-2">
                    {data.excellentProtectionUsedPoints > 0
                        ? t('dashboard.profile.standing.excellent_protection_used', {
                            points: data.excellentProtectionUsedPoints
                        })
                        : t('dashboard.profile.standing.excellent_protection_available')}
                </p>
            </div>

            <div className="rounded-xl border border-white/5 bg-white/5 p-4">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldAlert size={15} className="text-orange-500"/>
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                        {t('dashboard.profile.standing.active_points')}
                    </span>
                </div>

                <div className="flex items-end gap-2">
                    <span className="text-3xl font-black">
                        {data.activeRestrictionPoints}
                    </span>

                    <span className="text-[10px] text-gray-500 mb-1">
                        / 300 {t('dashboard.profile.standing.points')}
                    </span>
                </div>

                <p className="text-[10px] text-gray-500 mt-2">
                    {t('dashboard.profile.standing.active_points_desc')}
                </p>
            </div>

            <div className="rounded-xl border border-white/5 bg-white/5 p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Timer size={15} className="text-blue-500"/>
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                        {t('dashboard.profile.standing.decay')}
                    </span>
                </div>

                <div className="flex items-end gap-2">
                    <span className="text-3xl font-black">
                        {data.restrictionDecayPoints}
                    </span>

                    <span className="text-[10px] text-gray-500 mb-1">
                        {t('dashboard.profile.standing.points_per_week')}
                    </span>
                </div>

                <p className={`text-[10px] mt-2 ${data.restrictionDecayAvailable ? 'text-emerald-500' : 'text-gray-500'}`}>
                    {data.restrictionDecayAvailable
                        ? t('dashboard.profile.standing.decay_available')
                        : t('dashboard.profile.standing.decay_next', {
                            date: data.nextRestrictionDecayAt
                                ? new Date(data.nextRestrictionDecayAt).toLocaleString()
                                : '-'
                        })}
                </p>
            </div>
        </div>
    );
};

export default StandingStats;